"use client";

import {
  Box,
  Badge,
  GridItem,
  Flex,
  Image,
  Text,
  Grid,
  useToast,
  useColorModeValue,
  Button,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  ModalFooter,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Stack,
  Divider,
  IconButton,
  Skeleton,
  SkeletonText,
  SkeletonCircle
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { EyeIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import {
  BriefcaseIcon,
  CurrencyRupeeIcon,
  BuildingOffice2Icon,
  ClockIcon,
  CalendarIcon,
  TagIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import {
  fetchAdminJobListAsync,
  selectAdminJobList,
  selectAdminJobListTotal,
} from "./AdminJobListSlice";
import { updateBookmarkAsync } from "../../candidateDashboard/candidateSlice/SavedJobs/SavedJobsSlice";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../common/URLs";
import { TimeDifference } from "../../common/TimeDifference";
import { TimeDifferenceadmin } from "../../common/TimeDifferenceadmin";

export function AdminJobList() {
  const dispatch = useDispatch();
  const jobs = useSelector(selectAdminJobList);
  const totalJobs = useSelector(selectAdminJobListTotal);

  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedJob, setSelectedJob] = useState(null);

  const jobsPerPage = 15; // Number of jobs per page
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages
  const totalPages = Math.ceil(totalJobs / jobsPerPage);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };
  const [isLoading, setIsLoading] = useState(true);

  // Fetch jobs whenever the current page changes
  useEffect(() => {
    dispatch(fetchAdminJobListAsync(currentPage))
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [dispatch, currentPage]);

  const [bookmarkedJobs, setBookmarkedJobs] = useState({});

  const [appliedJobs, setAppliedJobs] = useState({});

  // Prefill applied jobs state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialAppliedJobs = {};
      jobs.forEach((job) => {
        initialAppliedJobs[job.uuid] = job.candidate_job_apply_count === 1;
      });
      setAppliedJobs(initialAppliedJobs);
    }
  }, [jobs]);

  // Prefill bookmark state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialBookmarks = {};
      jobs.forEach((job) => {
        initialBookmarks[job.uuid] = job.candidate_job_saved_count
          ? true
          : false;
      });
      setBookmarkedJobs(initialBookmarks);
    }
  }, [jobs]); // Run when jobs updates

  const toast = useToast();

  const handleBookmarkClick = async (jobId) => {
    const isCurrentlyBookmarked = bookmarkedJobs[jobId]; // Check current status
    const newStatus = isCurrentlyBookmarked ? "remove" : "save"; // Toggle between "save" and "remove"

    setBookmarkedJobs((prev) => ({
      ...prev,
      [jobId]: !isCurrentlyBookmarked, // Optimistic UI update
    }));

    try {
      await dispatch(
        updateBookmarkAsync({ job_uuid: jobId, status: newStatus })
      );

      // Show success toast on successful execution
      toast({
        title: `Job ${newStatus === "save" ? "saved" : "removed"
          } successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update bookmark:", error);

      setBookmarkedJobs((prev) => ({
        ...prev,
        [jobId]: isCurrentlyBookmarked, // Revert on failure
      }));

      // Show error toast on failure
      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Function to handle job selection and open modal
  const handleJobSelect = (job) => {
    setSelectedJob(job);
    onOpen();
  };

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const headerBg = useColorModeValue("blue.500", "blue.900");
  const headerTextColor = useColorModeValue("white", "gray.100");
  const subTextColor = useColorModeValue("blue.100", "blue.200");

  // Skeleton rows for loading state
  const skeletonRows = Array(jobsPerPage).fill(0).map((_, index) => (
    <Tr key={index}>
      <Td><Skeleton height="20px" /></Td>
      <Td><Skeleton height="20px" /></Td>
      <Td><Skeleton height="20px" /></Td>
      <Td><Skeleton height="20px" /></Td>
      <Td><Skeleton height="20px" /></Td>
      <Td><Skeleton height="20px" /></Td>
      <Td><Skeleton height="20px" width="60px" /></Td>
      <Td><Skeleton height="20px" width="60px" /></Td>
    </Tr>
  ));

  return (
    <div className="contentAdmin bg-adminbg rounded p-4">
      <div
        className="bg-white my-0 rounded-md"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex justify-between">
          <p className="text-xl text-black font-semibold px-3 py-7">
            Admin Jobs List
          </p>
        </div>
        <TableContainer>
          <Table size="sm">
            <Thead className="bg-gray-200">
              <Tr>
                <Th>ID</Th>
                <Th>Job Title</Th>
                <Th>Company</Th>
                <Th>Department</Th>
                <Th>Type</Th>
                <Th>Posted On</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                skeletonRows
              ) : jobs && jobs.length > 0 ? (
                jobs.map((job, index) => (
                  <Tr key={job.uuid}>
                    <Td>{index + 1}</Td>
                    <Td>
                      <div
                        className="text-blue-600 font-medium cursor-pointer"
                        onClick={() => handleJobSelect(job)}
                      >
                        {job?.job_title?.title || job.job_name}
                      </div>
                    </Td>
                    <Td>{job.company_name || "N/A"}</Td>
                    <Td>{job.department?.title || "N/A"}</Td>
                    <Td>{job.job_type || "N/A"}</Td>
                    <Td>
                      <TimeDifferenceadmin dateTimeString={job.job_post_date} />
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={job.status === "active" ? "green" : "red"}
                      >
                        {job.status}
                      </Badge>
                    </Td>
                    <Td>
                      <IconButton
                        aria-label="View details"
                        icon={<EyeIcon height={"20"} />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => handleJobSelect(job)}
                      />
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="8" className="text-center py-4">
                    No jobs available
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      {/* Pagination */}
      <GridItem colSpan={2} display="flex" justifyContent="center">
        <div className="flex w-full justify-evenly items-center mt-4">
          <Skeleton isLoaded={!isLoading} width="80px">
            <button
              className={`bg-gray-300 text-black px-4 py-2 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
          </Skeleton>

          <Skeleton isLoaded={!isLoading} width="120px">
            <span>
              Page {currentPage} of {totalPages}
            </span>
          </Skeleton>

          <Skeleton isLoaded={!isLoading} width="80px">
            <button
              className={`bg-blue-500 text-white px-4 py-2 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""
                }`}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </Skeleton>
        </div>
      </GridItem>

      {/* Job Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px" borderRadius="md" boxShadow="xl">
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            bg={headerBg}
            color={headerTextColor}
            borderTopRadius="md"
            py={4}
            px={6}
          >
            {selectedJob?.job_name || "Job Details"}
          </ModalHeader>
          <ModalCloseButton color={headerTextColor} />

          <ModalBody py={8} px={6}>
            {selectedJob ? (
              <Stack spacing={8}>
                {/* Company and Job Info */}
                <Flex alignItems="center" gap={5}>
                  <Box
                    bg="gray.50"
                    p={3}
                    borderRadius="md"
                    width="64px"
                    height="64px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="sm"
                    border="1px"
                    borderColor="gray.200"
                  >
                    {selectedJob.company_logo ? (
                      <Image
                        src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}/${selectedJob.company_logo}`}
                        alt={selectedJob.company_name}
                        fallback={
                          <BuildingOffice2Icon width={32} color="gray.400" />
                        }
                      />
                    ) : (
                      <BuildingOffice2Icon width={32} color="gray.400" />
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="xl" fontWeight="bold" mb={1}>
                      {selectedJob.company_name}
                    </Text>
                    <Badge
                      colorScheme={
                        selectedJob.status === "active" ? "green" : "red"
                      }
                      px={2}
                      py={1}
                      borderRadius="md"
                      textTransform="capitalize"
                    >
                      {selectedJob.status}
                    </Badge>
                  </Box>
                </Flex>

                {/* Job Details */}
                <Grid templateColumns="repeat(2, 1fr)" gap={8} rowGap={6}>
                  <Box>
                    <Flex alignItems="start" gap={2}>
                      <BriefcaseIcon width={18} />
                      <Text fontWeight="semibold" color="gray.700">
                        Job Type
                      </Text>
                    </Flex>
                    <Text className="capitalize" fontSize="md">
                      {selectedJob.job_type}
                    </Text>
                  </Box>

                  <Box>
                    <Flex alignItems="start" gap={2} mb={3}>
                      <TagIcon width={18} />
                      <Text fontWeight="semibold" color="gray.700">
                        Job Level
                      </Text>
                    </Flex>
                    <Text fontSize="md">{selectedJob.job_tag}</Text>
                  </Box>

                  <Box>
                    <Flex alignItems="start" gap={2} mb={3}>
                      <CurrencyRupeeIcon width={18} />
                      <Text fontWeight="semibold" color="gray.700">
                        Salary Range
                      </Text>
                    </Flex>
                    <Text fontSize="md">
                      {selectedJob.salary_min_amount ? (
                        <>
                          ₹{selectedJob.salary_min_amount.toLocaleString()} - ₹
                          {selectedJob.salary_max_amount.toLocaleString()}
                          <Text as="span" color="gray.600" ml={1}>
                            ({selectedJob.salary_per_type})
                          </Text>
                        </>
                      ) : (
                        "Not specified"
                      )}
                    </Text>
                  </Box>

                  <Box>
                    <Flex alignItems="start" gap={2} mb={3}>
                      <BuildingOffice2Icon width={18} />
                      <Text fontWeight="semibold" color="gray.700">
                        Department
                      </Text>
                    </Flex>
                    <Text fontSize="md">
                      {selectedJob.department?.title || "N/A"}
                    </Text>
                  </Box>
                </Grid>

                {/* Location Information */}
                <Box>
                  <Flex alignItems="start" gap={2} mb={4}>
                    <MapPinIcon width={18} />
                    <Text fontWeight="semibold" color="gray.700">
                      Location
                    </Text>
                  </Flex>
                  <Text fontSize="md" mb={3}>
                    {selectedJob.city?.name || "N/A"},{" "}
                    {selectedJob.state?.name || "N/A"},{" "}
                    {selectedJob.country?.name || "N/A"}
                  </Text>

                  {selectedJob.job_locations &&
                    selectedJob.job_locations.length > 0 && (
                      <Box mt={3}>
                        <Text
                          fontWeight="semibold"
                          fontSize="sm"
                          color="gray.600"
                          mb={2}
                        >
                          Additional Locations:
                        </Text>
                        <Flex gap={2} flexWrap="wrap">
                          {selectedJob.job_locations.map((location) => (
                            <Badge
                              key={location.uuid}
                              colorScheme="blue"
                              px={2}
                              py={1}
                              borderRadius="md"
                              fontSize="xs"
                            >
                              {location.job_location?.title || "Unknown"}
                            </Badge>
                          ))}
                        </Flex>
                      </Box>
                    )}
                </Box>

                {/* Date Information */}
                <Grid templateColumns="repeat(2, 1fr)" gap={8}>
                  <Box>
                    <Flex alignItems="start" gap={2} mb={3}>
                      <CalendarIcon width={18} />
                      <Text fontWeight="semibold" color="gray.700">
                        Posted On
                      </Text>
                    </Flex>
                    <Text fontSize="md">
                      {new Date(selectedJob.job_post_date).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </Text>
                  </Box>

                  <Box>
                    <Flex alignItems="start" gap={2} mb={3}>
                      <ClockIcon width={18} />
                      <Text fontWeight="semibold" color="gray.700">
                        Publication Status
                      </Text>
                    </Flex>
                    <Badge
                      colorScheme={
                        selectedJob.is_published ? "green" : "yellow"
                      }
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                    >
                      {selectedJob.is_published ? "Published" : "Not Published"}
                    </Badge>
                  </Box>
                </Grid>
              </Stack>
            ) : (
              <Stack spacing={6}>
                <Flex alignItems="center" gap={5}>
                  <SkeletonCircle size="64px" />
                  <Box>
                    <SkeletonText noOfLines={2} spacing="3" width="200px" />
                    <Skeleton width="80px" height="24px" mt={2} />
                  </Box>
                </Flex>

                <Grid templateColumns="repeat(2, 1fr)" gap={8} rowGap={6}>
                  {Array(4).fill(0).map((_, i) => (
                    <Box key={i}>
                      <Skeleton height="20px" width="100px" mb={2} />
                      <Skeleton height="16px" width="150px" />
                    </Box>
                  ))}
                </Grid>

                <Box>
                  <Skeleton height="20px" width="100px" mb={4} />
                  <Skeleton height="16px" width="250px" mb={3} />
                  <Skeleton height="16px" width="200px" />
                </Box>

                <Grid templateColumns="repeat(2, 1fr)" gap={8}>
                  {Array(2).fill(0).map((_, i) => (
                    <Box key={i}>
                      <Skeleton height="20px" width="120px" mb={2} />
                      <Skeleton height="16px" width="180px" />
                    </Box>
                  ))}
                </Grid>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter bg="gray.50" borderBottomRadius="md" py={4} px={6}>
            <Button
              colorScheme="blue"
              borderRadius="md"
              px={6}
              onClick={onClose}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}